/**
 * AutoBuildModal — one-click route builder modal.
 *
 * Wraps `POST /v1/routes/auto-build` in a two-step preview-then-create flow:
 *   1. PREVIEW — fills a form, calls the endpoint with `dryRun: true` and
 *      renders the projected clusters (groupKey | serviceClass | stops | est.
 *      minutes) plus a skipped-items breakdown.
 *   2. CREATE — when the preview yields ≥1 route, the user re-submits with
 *      `dryRun: false`; on success we toast, invalidate the routes query and
 *      close.
 *
 * Locked to the contract documented in `lib/api/delivery.ts > autoBuildRoutes`.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  autoBuildRoutes,
  type AutoBuildRoutesRequest,
  type AutoBuildRoutesResponse,
} from '../../lib/api/delivery';

interface AutoBuildModalProps {
  readonly onClose: () => void;
}

interface DraftState {
  readonly scheduledStart: string;
  readonly maxStopsPerRoute: number;
  readonly groupBy: 'destState' | 'destLga';
}

/** ISO seconds → "yyyy-MM-ddTHH:mm" string used by datetime-local inputs. */
function toLocalInputValue(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function todayAt9am(): string {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  return toLocalInputValue(d);
}

function extractServerMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body && typeof body.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function AutoBuildModal({ onClose }: AutoBuildModalProps): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftState>(() => ({
    scheduledStart: todayAt9am(),
    maxStopsPerRoute: 25,
    groupBy: 'destState',
  }));
  const [preview, setPreview] = useState<AutoBuildRoutesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const buildPayload = (dryRun: boolean): AutoBuildRoutesRequest => ({
    // datetime-local has no timezone; the server treats it as local civil time
    // and produces an ISO datetime. Append a `:00` seconds segment so the
    // string round-trips through `new Date(...)` cleanly on the wire.
    scheduledStart: new Date(draft.scheduledStart).toISOString(),
    maxStopsPerRoute: draft.maxStopsPerRoute,
    groupBy: draft.groupBy,
    dryRun,
  });

  const previewMut = useMutation({
    mutationFn: () => autoBuildRoutes(buildPayload(true)),
    onSuccess: (data) => {
      setPreview(data);
      setError(null);
      setSuccessMessage(null);
    },
    onError: (err) => setError(extractServerMessage(err, 'Preview failed')),
  });

  const createMut = useMutation({
    mutationFn: () => autoBuildRoutes(buildPayload(false)),
    onSuccess: async (data) => {
      setSuccessMessage(`Created ${data.created.length} draft route${data.created.length === 1 ? '' : 's'}`);
      setError(null);
      await qc.invalidateQueries({ queryKey: ['delivery', 'routes'] });
      // Brief delay so the success line is visible before the modal closes.
      window.setTimeout(() => onClose(), 600);
    },
    onError: (err) => setError(extractServerMessage(err, 'Create failed')),
  });

  const canCreate = useMemo(
    () => preview !== null && preview.dryRun && preview.created.length > 0,
    [preview],
  );
  const zeroResult = useMemo(
    () => preview !== null && preview.dryRun && preview.created.length === 0,
    [preview],
  );

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">Build today&apos;s routes</p>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Form */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <label htmlFor="ab-scheduled-start" className="text-[10px] font-semibold uppercase text-muted">
                Scheduled start
              </label>
              <input
                id="ab-scheduled-start"
                type="datetime-local"
                aria-label="Scheduled start"
                value={draft.scheduledStart}
                onChange={(e) => setDraft({ ...draft, scheduledStart: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="col-span-1">
              <label htmlFor="ab-max-stops" className="text-[10px] font-semibold uppercase text-muted">
                Max stops per route
              </label>
              <input
                id="ab-max-stops"
                type="number"
                min={1}
                max={100}
                aria-label="Max stops per route"
                value={draft.maxStopsPerRoute}
                onChange={(e) =>
                  setDraft({ ...draft, maxStopsPerRoute: Number(e.target.value) || 1 })
                }
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <p className="text-[10px] font-semibold uppercase text-muted mb-1">Group by</p>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ab-group-by"
                    value="destState"
                    checked={draft.groupBy === 'destState'}
                    onChange={() => setDraft({ ...draft, groupBy: 'destState' })}
                  />
                  By state
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ab-group-by"
                    value="destLga"
                    checked={draft.groupBy === 'destLga'}
                    onChange={() => setDraft({ ...draft, groupBy: 'destLga' })}
                  />
                  By LGA
                </label>
              </div>
            </div>
          </div>

          {/* Preview action */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setError(null);
                setSuccessMessage(null);
                previewMut.mutate();
              }}
              disabled={previewMut.isPending || draft.scheduledStart === ''}
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              {previewMut.isPending ? 'Previewing…' : 'Preview'}
            </button>
            {canCreate ? (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  createMut.mutate();
                }}
                disabled={createMut.isPending}
                className="px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {createMut.isPending
                  ? 'Creating…'
                  : `Create ${preview?.created.length ?? 0} draft${(preview?.created.length ?? 0) === 1 ? '' : 's'}`}
              </button>
            ) : null}
          </div>

          {/* Error */}
          {error !== null ? (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900"
            >
              {error}
            </div>
          ) : null}

          {/* Success */}
          {successMessage !== null ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
              {successMessage}
            </div>
          ) : null}

          {/* Preview body */}
          {preview !== null ? (
            zeroResult ? (
              <div
                data-testid="auto-build-zero-state"
                className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
              >
                <p className="font-semibold">No routes to build</p>
                <p>
                  {preview.totalConsidered} items considered, all skipped (
                  alreadyOnOpenRoute={preview.skipped.alreadyOnOpenRoute}, noAddress=
                  {preview.skipped.noAddress}, unknownServiceClass=
                  {preview.skipped.unknownServiceClass}).
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div
                  data-testid="auto-build-preview-table"
                  className="bg-white border border-border rounded-xl overflow-hidden"
                >
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                      <tr>
                        <th className="text-left px-4 py-2">Group key</th>
                        <th className="text-left px-4 py-2">Service class</th>
                        <th className="text-right px-4 py-2">Stops</th>
                        <th className="text-right px-4 py-2">Est. duration (min)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {preview.created.map((r, idx) => (
                        <tr key={`${r.groupKey}-${r.serviceClass}-${idx}`}>
                          <td className="px-4 py-2 text-xs font-mono">{r.groupKey}</td>
                          <td className="px-4 py-2 text-xs">{r.serviceClass}</td>
                          <td className="px-4 py-2 text-xs text-right">{r.stopCount}</td>
                          <td className="px-4 py-2 text-xs text-right">{r.estimatedDurationMinutes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted">
                  {preview.totalConsidered} items considered ·{' '}
                  {preview.skipped.alreadyOnOpenRoute} already on open routes ·{' '}
                  {preview.skipped.noAddress} with no address ·{' '}
                  {preview.skipped.unknownServiceClass} unknown service class
                </p>
              </div>
            )
          ) : null}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
